import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

export interface Announcement {
  id: number;
  title: string;
  body: string;
  authorId: number | null;
  createdAt: string | null;
}

const key = (cohortId: string) => ['announcements', cohortId] as const;

export function useAnnouncements(cohortId: string | undefined) {
  return useQuery<ReadonlyArray<Announcement>>({
    queryKey: key(cohortId ?? ''),
    enabled: !!cohortId,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<{ items: Announcement[] }>('/cohorts/{id}/announcements', {
        method: 'GET', pathParams: { id: cohortId ?? '' }, signal,
      });
      return resp.items ?? [];
    },
  });
}

export function useCreateAnnouncement(cohortId: string) {
  const qc = useQueryClient();
  return useMutation<Announcement, Error, { title: string; body: string }>({
    mutationFn: (input) =>
      apiFetch<Announcement>('/cohorts/{id}/announcements', {
        method: 'POST', pathParams: { id: cohortId }, body: input,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key(cohortId) }); },
  });
}

export function useMyAnnouncements() {
  return useQuery<ReadonlyArray<Announcement & { cohortId: number }>>({
    queryKey: ['announcements', 'me'],
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<{ items: (Announcement & { cohortId: number })[] }>('/me/announcements', { method: 'GET', signal });
      return resp.items ?? [];
    },
  });
}
